<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">OTA Files</h1>
                <div class="d-flex align-center ga-3">
                    <v-btn-toggle v-model="showFilter" density="compact" variant="outlined" color="primary"
                                  @update:model-value="currentPage = 1; fetchFiles()">
                        <v-btn value="disabled" size="small">
                            <v-icon start :icon="showFilter === 'disabled' ? 'mdi-eye' : 'mdi-eye-off'"/>{{ showFilter === 'disabled' ? 'Hide Disabled' : 'Show Disabled' }}
                        </v-btn>
                        <v-btn value="trashed" size="small">
                            <v-icon start :icon="showFilter === 'trashed' ? 'mdi-delete-restore' : 'mdi-delete-outline'"/>{{ showFilter === 'trashed' ? 'Hide Deleted' : 'Show Deleted' }}
                        </v-btn>
                    </v-btn-toggle>
                    <v-btn color="black" prepend-icon="mdi-plus" @click="()=>{selectedFile = null; dialogSwitch = true}">
                        Add
                    </v-btn>
                </div>
            </v-col>
        </v-row>
        <h3></h3>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th class="cursor-pointer" @click="toggleSort('id')">
                            ID <v-icon v-if="sortBy === 'id'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('sku')">
                            Product SKU <v-icon v-if="sortBy === 'sku'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('component_name')">
                            OTA Component <v-icon v-if="sortBy === 'component_name'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('name')">
                            Name <v-icon v-if="sortBy === 'name'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('file_name')">
                            File <v-icon v-if="sortBy === 'file_name'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('priority')">
                            Priority <v-icon v-if="sortBy === 'priority'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('version')">
                            Version <v-icon v-if="sortBy === 'version'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('updated_at')">
                            Last Updated <v-icon v-if="sortBy === 'updated_at'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th>Notes</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="otaFile in otaFiles" v-if="otaFiles.length" :key="otaFile.id"
                        class="text-left" :class="{'bg-red-lighten-5': otaFile.deleted_at}">
                        <td>{{ otaFile.id }}</td>
                        <td>{{ otaFile.sku }}</td>
                        <td>{{ otaFile.component_name }}</td>
                        <td>{{ otaFile.name }}</td>
                        <td>{{ otaFile.file_name }} ({{ (parseInt(otaFile.file_size) / 1024).toFixed(1) }} KB)</td>
                        <td>
                            <v-icon :icon="`mdi-${otaFile.priority?'check':'close'}-circle`" size="small"/>
                        </td>
                        <td>{{ otaFile.version }}</td>
                        <td>{{ formatDateTime(otaFile.updated_at) }}</td>
                        <td class="text-caption" style="max-width: 200px;">
                            <v-tooltip v-if="otaFile.notes" :text="otaFile.notes" location="top" max-width="300">
                                <template v-slot:activator="{ props }">
                                    <span v-bind="props" class="d-inline-block text-truncate" style="max-width: 200px;">{{ otaFile.notes }}</span>
                                </template>
                            </v-tooltip>
                        </td>
                        <td>
                            <v-btn-group v-if="!otaFile.deleted_at" density="compact" divided variant="outlined">
                                <v-btn :disabled="dialogSwitch || otaFile.active_campaign_count > 0" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editFile(otaFile)">Edit
                                    <v-tooltip v-if="otaFile.active_campaign_count > 0" activator="parent" location="top">Disable active campaign before editing</v-tooltip>
                                </v-btn>
                                <v-btn :class="otaFile.status ? 'text-grey' : 'text-success'" size="x-small"
                                       :prepend-icon="otaFile.status ? 'mdi-eye-off' : 'mdi-eye'"
                                       @click="toggleFileStatus(otaFile)">{{ otaFile.status ? 'Disable' : 'Enable' }}
                                </v-btn>
                                <v-btn :disabled="otaFile.active_campaign_count > 0" class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteFile(otaFile)">Delete
                                    <v-tooltip v-if="otaFile.active_campaign_count > 0" activator="parent" location="top">Disable active campaign before deleting</v-tooltip>
                                </v-btn>
                            </v-btn-group>
                            <v-btn-group v-else density="compact" divided variant="outlined">
                                <v-menu>
                                    <template v-slot:activator="{ props }">
                                        <v-btn icon="mdi-dots-vertical" v-bind="props" variant="text" size="x-small"/>
                                    </template>
                                    <v-list density="compact">
                                        <v-list-item
                                            title="Permanently Delete"
                                            prepend-icon="mdi-delete-forever"
                                            class="text-error"
                                            @click="forceDeleteFile(otaFile)"/>
                                    </v-list>
                                </v-menu>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="10">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="10">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchFiles"
                />
            </v-col>
        </v-row>
    </v-container>

    <otaFile v-if="dialogSwitch" v-model="dialogSwitch" :otaFile="selectedFile"
             @addFile="(newFile)=> otaFiles.unshift(newFile)"
             @updateFile="(updatedFile) => {
                 const idx = otaFiles.findIndex(f => f.id === updatedFile.id);
                 if (idx !== -1) otaFiles[idx] = updatedFile;
             }"/>
</template>

<script setup>
import {onMounted, ref, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import OtaFile from "@/components/dialogs/ota/File.vue";

const otaFiles = ref([]);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const {showSnackbar} = useSnackbarStore();
const dialogSwitch = ref(false);
const selectedFile = ref(null);
const showFilter = ref(undefined);
const sortBy = ref('updated_at');
const sortDir = ref('desc');
const sortIcon = computed(() => sortDir.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down');

const toggleSort = (column) => {
    if (sortBy.value === column) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column;
        sortDir.value = 'asc';
    }
    currentPage.value = 1;
    fetchFiles();
};

onMounted(() => {
    fetchFiles();
});

const fetchFiles = () => {
    const params = {page: currentPage.value, sort_by: sortBy.value, sort_dir: sortDir.value};
    if (showFilter.value === 'disabled') params.show_disabled = 1;
    if (showFilter.value === 'trashed') params.show_trashed = 1;
    send('get', `admin/ota/files`, params)
        .then((response) => {
            otaFiles.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('OTA Files could not be loaded', 'error')
        })
        .finally(() => {
            loading.value = false
        })
}
const editFile = (otaFile) => {
    selectedFile.value = otaFile;
    dialogSwitch.value = true;
}
const toggleFileStatus = (otaFile) => {
    const newStatus = otaFile.status ? 0 : 1;
    send('put', `admin/ota/files/${otaFile.id}`, {status: newStatus})
        .then((response) => {
            if (response.data.success) {
                if (!showFilter.value && !newStatus) {
                    const idx = otaFiles.value.findIndex(f => f.id === otaFile.id);
                    if (idx !== -1) otaFiles.value.splice(idx, 1);
                } else {
                    otaFile.status = newStatus;
                }
                showSnackbar(response.data.message ?? `OTA File ${newStatus ? 'enabled' : 'disabled'}`);
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? 'Could not update status', 'error');
        });
}
const deleteFile = (otaFile) => {
    if (confirm(`Are you sure to delete "${otaFile.name}" OTA File?`)) {
        send('delete', `admin/ota/files/${otaFile.id}`)
            .then((response) => {
                if (response.data.success) {
                    const idx = otaFiles.value.findIndex(f => f.id === otaFile.id);
                    if (idx !== -1) otaFiles.value.splice(idx, 1);
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response?.data?.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${otaFile.name}" OTA File could not be deleted`, 'error')
                }
            })
    }
}
const forceDeleteFile = (otaFile) => {
    if (!confirm(`Permanently delete "${otaFile.name}"? This will remove the file and detach it from all packages. This cannot be undone.`)) return;
    send('post', `admin/ota/files/${otaFile.id}/force-delete`)
        .then((response) => {
            if (response.data.success) {
                const idx = otaFiles.value.findIndex(f => f.id === otaFile.id);
                if (idx !== -1) otaFiles.value.splice(idx, 1);
                showSnackbar(response.data.message);
            }
        })
        .catch((error) => showSnackbar(error.response?.data?.message || 'Failed to permanently delete file', 'error'));
}
</script>
